import * as React from 'react';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import uuid from 'src/nativeModules/UUID';
import ScreenContext from '../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import Events from 'src/logging/Events';
import AVTextInput from '../../elements/AVTextInput';
import RoundedButton, {ButtonType} from '../../elements/RoundedButton';
import withIsConnected from '../../hoc/withIsConnected';
import Styles from '../../Styles';
import BaseScreen from '../BaseScreen';
import type {ScreenProps, IsConnectedProps} from 'src/types/Screen';
import KeyboardAvoidingView from '../../elements/365KeyboardAvoidingView';
import ActionsFactory from 'src/actions/ActionsFactory';
import AccountStore from 'src/stores/AccountStore';
import {StyleSheet, View, ScrollView} from 'react-native';
import Localized from 'src/constants/AppStrings';
import {alertError, alertSuccess} from '../../helpers/AlertHelper';
type ChangePasswordScreenProps = IsConnectedProps;
type ChangePasswordScreenState = {
  password: string;
  confirmPassword: string;
  currentPassword: string;
};

class ChangePasswordScreen extends React.Component<
  ChangePasswordScreenProps,
  ChangePasswordScreenState
> {
  static contextType = ScreenContext;

  constructor(props: ChangePasswordScreenProps) {
    super(props);
    this.state = {
      password: '',
      confirmPassword: '',
      currentPassword: '',
    };
    this.saveClick = this.saveClick.bind(this);
    this.validate = this.validate.bind(this);
    this.changePassword = this.changePassword.bind(this);
  }

  saveClick() {
    if (!this.validate()) {
      alertError(Localized.Errors.must_enter_matching_password);
    } else {
      this.changePassword();
    }
  }

  validate() {
    return (
      this.state.password &&
      this.state.confirmPassword &&
      this.state.password === this.state.confirmPassword
    );
  }

  async changePassword() {
    if (!this.state.currentPassword) {
      alertError(Localized.Errors.current_password_is_not_correct);
    } else {
      this.context.actions.showSpinner();

      try {
        const response =
          await ActionsFactory.getAccountActions().changePassword(
            AccountStore.getAccountId(),
            AccountStore.getUsername(),
            this.state.currentPassword,
            this.state.password,
          );

        if (response.Success) {
          alertSuccess(Localized.Success.password_changed);
          NavActions.pop();
        } else if (response.InvalidPassword) {
          alertError(Localized.Errors.current_password_is_not_correct);
        } else {
          alertError(Localized.Errors.unable_to_set_password);
        }
      } catch (error) {
        const guid = await uuid.getRandomUUID();
        Events.Error.trackEvent(
          'Exception',
          'ChangePasswordScreen:ChangePassword',
          error.message ? error.message : error.toString(),
          guid,
        );
        alertError(Localized.Errors.unable_to_set_password, guid);
      } finally {
        this.context.actions.hideSpinner();
      }
    }
  }

  render() {
    return (
      <BaseScreen title={Localized.Labels.change_password}>
        <KeyboardAvoidingView
          style={Styles.Style.flex}
          behavior="height"
          insideTab
        >
          <ScrollView
            contentContainerStyle={Styles.Style.fex}
            keyboardDismissMode="interactive"
            automaticallyAdjustContentInsets={false}
            keyboardShouldPersistTaps="handled"
          >
            <View style={[styles.content, Styles.Style.maxWidthContainer]}>
              <AVTextInput
                label={Localized.Labels.current_password}
                onChangeText={(text) =>
                  this.setState({
                    currentPassword: text,
                  })
                }
                accessibilityLabel="Current Password"
                value={this.state.currentPassword}
                secureTextEntry
              />
              <AVTextInput
                label={Localized.Labels.password}
                onChangeText={(text) =>
                  this.setState({
                    password: text,
                  })
                }
                accessibilityLabel="New Password"
                value={this.state.password}
                secureTextEntry
              />
              <AVTextInput
                label={Localized.Labels.confirm_password}
                onChangeText={(text) =>
                  this.setState({
                    confirmPassword: text,
                  })
                }
                accessibilityLabel="Confirm Password"
                value={this.state.confirmPassword}
                secureTextEntry
              />
            </View>
          </ScrollView>
          {this.props.isConnected && (
            <RoundedButton
              buttonType={ButtonType.action}
              accessibilityLabel="Save"
              onPress={this.saveClick}
              text={Localized.Buttons.save}
            />
          )}
        </KeyboardAvoidingView>
      </BaseScreen>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
    paddingHorizontal: Styles.Spacing.m3,
    paddingTop: Styles.Spacing.m1,
  },
});
export default withForwardedNavigationParams()(
  withIsConnected(ChangePasswordScreen),
);
